// PostgreSQL API Configuration
// This module provides API endpoints for database operations with rate limiting, caching, and deduplication

// Use REACT_APP_API_URL if set, otherwise use empty string for relative URLs (same domain)
// This allows the app to work both in development (localhost:3001) and production (custom domain)
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== "" ? process.env.REACT_APP_API_URL : "";

// Request cache to avoid duplicate requests
const requestCache = new Map();
const CACHE_DURATION = 10000; // 10 seconds cache (increased from 5)

// Request deduplication - prevent multiple simultaneous requests to same endpoint
const pendingRequests = new Map();

// Rate limiter: max 50 requests per second (increased to handle concurrent requests)
class RateLimiter {
  constructor(maxRequests = 50, windowMs = 1000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = [];
  }

  async wait() {
    const now = Date.now();

    // Remove old requests outside the window
    this.requests = this.requests.filter((time) => now - time < this.windowMs);

    // If we've hit the limit, wait
    if (this.requests.length >= this.maxRequests) {
      const oldestRequest = this.requests[0];
      const waitTime = this.windowMs - (now - oldestRequest) + 10;
      if (waitTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, waitTime));
        return this.wait(); // Recursively check again
      }
    }

    // Record this request
    this.requests.push(Date.now());
  }
}

const rateLimiter = new RateLimiter(50, 1000);

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
  try {
    return sessionStorage.getItem("safir_auth_token");
  } catch (error) {
    return null;
  }
};

// Helper function to make API calls with rate limiting, caching, and deduplication
export const apiCall = async (endpoint, options = {}, retries = 3) => {
  // Only cache GET requests
  const isGetRequest = !options.method || options.method === "GET";
  const cacheKey = `${endpoint}`;

  // Check cache for GET requests
  if (isGetRequest && requestCache.has(cacheKey)) {
    const cached = requestCache.get(cacheKey);
    if (Date.now() - cached.timestamp < CACHE_DURATION) {
      // console.log(`📦 Cache hit for ${endpoint}`);
      return cached.data;
    } else {
      requestCache.delete(cacheKey);
    }
  }

  // Check if request is already pending (deduplication)
  if (isGetRequest && pendingRequests.has(cacheKey)) {
    // console.log(`⏳ Waiting for pending request: ${endpoint}`);
    return pendingRequests.get(cacheKey);
  }

  // Create the actual request promise
  const requestPromise = (async () => {
    // Apply rate limiting
    await rateLimiter.wait();

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const controller = new AbortController();
        // Increased timeout to 90 seconds to match server statement timeout (60s) + buffer
        const timeout = setTimeout(() => controller.abort(), 90000);

        // Get auth token and include it in headers if available
        const token = getAuthToken();
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        };

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          headers,
          signal: controller.signal,
          ...options,
        });

        clearTimeout(timeout);

        if (!response.ok) {
          // Handle specific error codes
          if (response.status === 429) {
            // Rate limited - wait longer and retry
            const waitTime = parseInt(
              response.headers.get("Retry-After") || "2000"
            );
            console.warn(
              `⚠️ Rate limited on ${endpoint}, waiting ${waitTime}ms`
            );
            await new Promise((resolve) => setTimeout(resolve, waitTime));
            continue;
          }

          if (response.status >= 500 && attempt < retries - 1) {
            // Server error - retry with backoff
            const backoffTime = 1000 * Math.pow(2, attempt);
            console.warn(
              `⚠️ Server error ${response.status} on ${endpoint}, retrying in ${backoffTime}ms`
            );
            await new Promise((resolve) => setTimeout(resolve, backoffTime));
            continue;
          }

          throw new Error(
            `API Error: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        // Cache successful GET responses
        if (isGetRequest) {
          requestCache.set(cacheKey, {
            data,
            timestamp: Date.now(),
          });
        }

        return data;
      } catch (error) {
        // Handle network errors and timeouts
        if (error.name === "AbortError") {
          console.warn(
            `⏱️ API call timeout for ${endpoint} (attempt ${
              attempt + 1
            }/${retries})`
          );
          if (attempt < retries - 1) {
            const backoffTime = 1000 * Math.pow(2, attempt);
            await new Promise((resolve) => setTimeout(resolve, backoffTime));
            continue;
          }
        }

        if (attempt === retries - 1) {
          console.error(
            `❌ API call failed for ${endpoint} after ${retries} attempts:`,
            error.message
          );
          throw error;
        }

        // Exponential backoff for retries
        const backoffTime = 1000 * Math.pow(2, attempt);
        console.warn(
          `⚠️ Retrying ${endpoint} in ${backoffTime}ms (attempt ${
            attempt + 1
          }/${retries})`
        );
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
      }
    }
  })();

  // Store pending request for deduplication
  if (isGetRequest) {
    pendingRequests.set(cacheKey, requestPromise);

    // Remove from pending when done
    requestPromise.finally(() => {
      pendingRequests.delete(cacheKey);
    });
  }

  return requestPromise;
};

// Clear cache function (useful for manual refresh)
export const clearCache = (endpoint = null) => {
  if (endpoint) {
    requestCache.delete(endpoint);
  } else {
    requestCache.clear();
  }
};

// Export API base URL for use in services
export { API_BASE_URL };
